import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import {
  Table,
  TableHead,
  TableSortLabel,
  TableBody,
  TableRow,
  TableCell,
  Typography,
  AppBar,
  Toolbar,
  TableFooter,
  Fade,
  Button,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import uuid from 'uuid';
import { useHashLocation } from 'wouter/use-hash-location';
import { useLocation } from 'wouter';
import withDataResolver from 'src/components/common/withDataResolver';
import massActionApi from 'src/utils/api/massActionApi';
import { useMassActionState } from 'src/components/massAction/MassActionStateContext';

import CsvExportButton from './MassActionCsvExportButton';
import Pagination from 'src/components/common/pagination/Pagination';
import { COLUMS_SELECTOR, CONTENT_SELECTOR } from '../MassActionEditor';
import useFetch from 'src/hooks/useFetch';
import utils from 'src/utils/utils';
import { useCurrentAppLn } from 'src/hooks';

const useStyles = makeStyles()((theme, _params, classes) => ({
  active: { color: theme.palette.primary.main },
  root: {
    [`&.${classes.active}`]: {
      color: theme.palette.primary.main,
      '& svg path': { color: theme.palette.primary.main },
    },
  },
}));

const StyledTableSortLabel = ({ children, ...props }) => {
  const { classes } = useStyles();
  return (
    <TableSortLabel
      classes={{ root: classes.root, active: classes.active }}
      color="primary"
      {...props}
    >
      {children}
    </TableSortLabel>
  );
};

StyledTableSortLabel.propTypes = {
  children: PropTypes.any.isRequired,
};

const appBarUseStyles = makeStyles()(() => ({
  appBar: {
    position: 'relative',
    textAlign: 'left',
  },
  flex: { flex: 1 },
}));

const StyledAppBar = ({ currentName, currentMassActionId }) => {
  const { classes } = appBarUseStyles();
  const { t } = useTranslation();

  return (
    <AppBar className={classes.appBar} color="primary">
      <Toolbar style={{ paddingLeft: '8px' }}>
        <div className={classes.flex}>
          <Typography variant="h5" color="inherit">
            {t(`smartmessaging.massAction.requestResultList.title`)}
          </Typography>
          <Typography variant="body2" color="inherit">
            {currentName}
            {!!currentMassActionId && ` (${currentMassActionId})`}
          </Typography>
        </div>
        <div
          style={{
            boxShadow: 'inset 0.3px 0px 0px 0px #ffffffd5, inset -0.3px 0px 0px 0px #ffffffd5',
            marginLeft: '8px',
            marginRight: '8px',
            padding: '0 8px',
          }}
        >
          <CsvExportButton />
        </div>
      </Toolbar>
    </AppBar>
  );
};

StyledAppBar.propTypes = {
  currentName: PropTypes.string.isRequired,
  currentMassActionId: PropTypes.number.isRequired,
};

const sorters = {
  default: orderBy => ({
    asc: (a, b) => {
      if (a[orderBy] < b[orderBy]) return -1;
      if (a[orderBy] > b[orderBy]) return 1;
      return 0;
    },
    desc: (a, b) => {
      if (a[orderBy] > b[orderBy]) return -1;
      if (a[orderBy] < b[orderBy]) return 1;
      return 0;
    },
  }),
  STRING: orderBy => ({
    asc: (a, b) => {
      if (a[orderBy].toLowerCase().trim() < b[orderBy].toLowerCase().trim()) return -1;
      if (a[orderBy].toLowerCase().trim() > b[orderBy].toLowerCase().trim()) return 1;
      return 0;
    },
    desc: (a, b) => {
      if (a[orderBy].toLowerCase().trim() > b[orderBy].toLowerCase().trim()) return -1;
      if (a[orderBy].toLowerCase().trim() < b[orderBy].toLowerCase().trim()) return 1;
      return 0;
    },
  }),
  INTEGER: orderBy => ({
    asc: (a, b) => {
      if (parseInt(a[orderBy], 10) < parseInt(b[orderBy], 10)) return -1;
      if (parseInt(a[orderBy], 10) > parseInt(b[orderBy], 10)) return 1;
      return 0;
    },
    desc: (a, b) => {
      if (parseInt(a[orderBy], 10) > parseInt(b[orderBy], 10)) return -1;
      if (parseInt(a[orderBy], 10) < parseInt(b[orderBy], 10)) return 1;
      return 0;
    },
  }),
  DATE: orderBy => ({
    asc: (a, b) => {
      const aDate = new Date(a[orderBy]);
      const bDate = new Date(b[orderBy]);
      if (!aDate && !bDate) return 0;
      if (!aDate) return -1;
      if (!bDate) return 1;
      if (aDate.getTime() < bDate.getTime()) return -1;
      if (aDate.getTime() > bDate.getTime()) return 1;
      return 0;
    },
    desc: (a, b) => {
      const aDate = new Date(a[orderBy]);
      const bDate = new Date(b[orderBy]);
      if (!aDate && !bDate) return 0;
      if (!aDate) return 1;
      if (!bDate) return -1;
      if (aDate.getTime() < bDate.getTime()) return 1;
      if (aDate.getTime() > bDate.getTime()) return -1;
      return 0;
    },
  }),
};

const getSorter = ({ order, orderBy, type }) =>
  sorters[type] ? sorters[type](orderBy)[order] : sorters.default(orderBy)[order];

const entryRenders = {
  field5: (value, { civilities, lang }) => {
    if (!value) return '-';
    const civilityOption = civilities?.find(civ => civ.key === value);
    if (!civilityOption) return value;
    return utils.getCivilityLabel(civilityOption, lang);
  }, // field5 = Civility
};

const renderEntry = (entry, col, options) =>
  entryRenders[col] ? entryRenders[col](entry[col], options) : entry[col];

const getUngroupedAvailableFields = availableResultfields =>
  Object.values(availableResultfields).reduce(
    (ungrouped, current) => ({
      ...ungrouped,
      ...current.reduce((result, field) => ({ ...result, [field.value]: field.type }), {}),
    }),
    {}
  );

const isIdColumnSelected = selectedResultfields => {
  const groups = Object.values(selectedResultfields);
  const fields = groups.reduce(
    (acc, group) => [...acc, ...Object.values(group).map(field => field.value)],
    []
  );
  return fields.indexOf('field0') !== -1;
};

function MassActionRequestResultRender({
  result,
  goToStep,
  currentName,
  idColumnIsSelected,
  ungroupedAvailableField,
  currentMassActionId,
  isWritable,
}) {
  const { t } = useTranslation();
  const [sortConfig, setSortConfig] = useState({
    order: 'asc',
    orderBy: (result.length && Object.keys(result[0])[0]) || 'id',
  });

  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [page, setPage] = useState(0);
  const cols = (result && result.length && Object.keys(result[0])) || [];

  const [hLocation, hNavigate] = useHashLocation();
  const [location] = useLocation();

  const { data: civilities } = useFetch(
    { url: '/civility/all' },
    { enabled: cols.includes('field5') } // if civility field is selected
  );
  const { locale: currentLang } = useCurrentAppLn();

  if (!!currentMassActionId && location === '/oneoff/create' && hLocation === '/') {
    hNavigate(`${currentMassActionId}`);
    return null;
  }

  return (
    <div style={{ display: 'flex', flex: 1, flexDirection: 'column', height: '100%' }}>
      <StyledAppBar
        goToStep={goToStep}
        currentName={currentName}
        currentMassActionId={currentMassActionId}
        isWritable={isWritable}
      />
      <Fade in timeout={500}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            overflow: 'hidden',
            padding: '10px ',
          }}
        >
          <div
            style={{
              margin: '2px 0 0 4px',
              overflow: 'auto',
              flex: 1,
              height: '100%',
            }}
          >
            {!!result && !!result.length && (
              <Table>
                <TableHead>
                  <TableRow>
                    {cols
                      .filter(column => idColumnIsSelected || column !== 'field0')
                      .map(c => (
                        <TableCell
                          key={c}
                          style={{
                            position: 'sticky',
                            top: 0,
                            background: '#ffffff',
                            padding: '4px',
                          }}
                        >
                          <StyledTableSortLabel
                            key={c}
                            active={sortConfig.orderBy === c}
                            direction={sortConfig.order}
                            onClick={() => {
                              setSortConfig({
                                order: sortConfig.order === 'asc' ? 'desc' : 'asc',
                                orderBy: c,
                              });
                            }}
                          >
                            {t(`smartmessaging.massAction.columnLabel.${c}`)}
                          </StyledTableSortLabel>
                        </TableCell>
                      ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {result
                    .sort(
                      getSorter({
                        order: sortConfig.order,
                        orderBy: sortConfig.orderBy,
                        type: ungroupedAvailableField[sortConfig.orderBy] || 'default',
                      })
                    )
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map(singleEntry => (
                      <TableRow key={uuid()} hover>
                        {cols
                          .filter(column => idColumnIsSelected || column !== 'field0')
                          .map(c => (
                            <TableCell style={{ padding: '4px' }} key={uuid()}>
                              {renderEntry(singleEntry, c, { civilities, lang: currentLang })}
                            </TableCell>
                          ))}
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            )}
            {(!result || !result.length) && (
              <Typography color="textSecondary">
                {t('smartmessaging.massAction.result.empty')}
              </Typography>
            )}
          </div>
          <Table>
            <TableFooter>
              <TableRow>
                <Pagination
                  pages={result.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  setPage={setPage}
                  setRowsPerPage={setRowsPerPage}
                />
              </TableRow>
            </TableFooter>
          </Table>
          <div style={{ textAlign: 'right', paddingTop: '12px' }}>
            <Button
              variant="text"
              color="primary"
              style={{ margin: '2px' }}
              onClick={e => {
                e.preventDefault();
                goToStep(COLUMS_SELECTOR);
              }}
            >
              {t('smartmessaging.buttonLabel.previous')}
            </Button>
            <Button
              variant="contained"
              color="primary"
              style={{ margin: '2px' }}
              onClick={e => {
                e.preventDefault();
                goToStep(CONTENT_SELECTOR);
              }}
            >
              {t('smartmessaging.buttonLabel.next')}
            </Button>
          </div>
        </div>
      </Fade>
    </div>
  );
}

MassActionRequestResultRender.propTypes = {
  result: PropTypes.array.isRequired,
  goToStep: PropTypes.func.isRequired,
  currentName: PropTypes.string.isRequired,
  currentMassActionId: PropTypes.number.isRequired,
  idColumnIsSelected: PropTypes.bool.isRequired,
  ungroupedAvailableField: PropTypes.object.isRequired,
  isWritable: PropTypes.bool.isRequired,
};

const resolvers = {
  resolve: props => async callApi => {
    const result = await callApi(massActionApi.getMassActionExport, [
      props.recipeId,
    ]).catch(() => []);

    return { result };
  },
  onResolved: props => async resolvedData => {
    props.setResult(resolvedData.result);
  },
  getLoaderText: () => i18next.t('smartmessaging.massAction.requestExecutionPending'),
};

const WithData = withDataResolver(resolvers)(MassActionRequestResultRender);

function MassActionRequestResult({ goToStep, ...other }) {
  const [
    {
      currentRecipeId,
      requestModel,
      currentMassActionId,
      currentName,
      selectedResultfields,
      availableResultfields,
    },
  ] = useMassActionState();
  const [result, setResult] = useState([]);
  const [contents, setContents] = useState(null);

  return (
    <WithData
      result={result}
      setResult={setResult}
      contents={contents}
      setContents={setContents}
      recipeId={currentRecipeId}
      massActionId={currentMassActionId}
      massActionRequestModelId={requestModel?.id}
      idColumnIsSelected={isIdColumnSelected(selectedResultfields)}
      currentName={currentName}
      currentMassActionId={currentMassActionId}
      ungroupedAvailableField={getUngroupedAvailableFields(availableResultfields)}
      goToStep={goToStep}
      {...other}
    />
  );
}

MassActionRequestResult.propTypes = {
  goToStep: PropTypes.func.isRequired,
};

export default MassActionRequestResult;
